import { cases } from '@st/redux'
import type { CustomQuestionCategory, JSON, QuestionnaireTopicSection } from '@st/sdk'
import {
  Accordion,
  AccordionItem,
  Button,
  CaretDownIcon,
  CloudUploadIcon,
  EyeIcon,
  IconTip,
  Spinner,
  Tab,
  TabGroup,
  TextArea,
  ToggleSwitch,
  WarningIcon,
  Wizard
} from '@st/theme'
import { toMaybeStr } from '@st/util/cast'
import { inflect } from '@st/util/inflect'
import { useCaptureBrowserResource } from '~/features/browser-resources/browser-resource-boundary'
import { OrganizationSettingsScaffold } from '~/features/st-organizations/st-organization-settings-scaffold'
import type { ReactNode } from 'react'
import { useProcess, useSelect } from '~/store'
import {
  CLIENT_ORGANIZER_DASHBOARD_NOTE_KEY,
  CLIENT_ORGANIZER_MFA_ENABLED_KEY,
  DEFAULT_CLIENT_ORGANIZER_DASHBOARD_NOTE,
  RETURN_TYPES,
  TAX_RETURN_DELIVERY_KBA_ENABLED_KEY,
  groupCustomQuestionCategories,
  includedQuestionCount,
  isCategoryVisible,
  sectionCategoryCount,
  topicCategoryCount,
  visibleSectionCategoryCount
} from '../folder-settings'
import {
  selClientOrganizerSettingsPage,
  type ClientOrganizerSettingsBody,
  type ClientOrganizerSettingsForm,
  type ClientOrganizerSettingsMessage,
  type LetterView
} from '../module'

type Send = (message: ClientOrganizerSettingsMessage) => void

export function ClientOrganizerSettingsPage() {
  const page = useSelect('clientOrganizerSettings', selClientOrganizerSettingsPage)
  const { send } = useProcess('clientOrganizerSettings')
  const captureBrowserResource = useCaptureBrowserResource()

  return (
    <OrganizationSettingsScaffold title="Settings">
      <div className="flex max-w-3xl flex-col gap-4">
        <h2 className="mb-5 text-xl text-gray-800">Client Organizers</h2>

        <TabGroup>
          {RETURN_TYPES.map((returnType) => (
            <Tab
              className="w-20"
              key={returnType}
              selected={returnType == page.returnType}
              onClick={() => send({ type: 'returnTypeSelected', returnType })}
            >
              {returnType}
            </Tab>
          ))}
        </TabGroup>

        <SettingsBody
          body={page.body}
          send={send}
          uploadLetter={(file) =>
            send({ type: 'letterUploadRequested', file: captureBrowserResource(file) })
          }
        />
      </div>
    </OrganizationSettingsScaffold>
  )
}

function SettingsBody({
  body,
  send,
  uploadLetter
}: {
  body: ClientOrganizerSettingsBody
  send: Send
  uploadLetter: (file: File) => void
}) {
  return (
    <div className="mt-4 flex flex-col gap-8">
      {cases(body, 'kind', {
        loading: () => <Spinner className="mx-auto size-8 h-20 fill-blue-500 text-blue-100" />,
        failed: () => (
          <div className="flex flex-col gap-4">
            <div className="text-base text-red-600">Unable to load client organizer settings.</div>
            <div>
              <Button variant="default" onClick={() => send({ type: 'retryRequested' })}>
                Retry
              </Button>
            </div>
          </div>
        ),
        form: (form) => (
          <SettingsForm key={form.scopeKey} form={form} send={send} uploadLetter={uploadLetter} />
        )
      })}
    </div>
  )
}

function SettingsForm({
  form,
  send,
  uploadLetter
}: {
  form: ClientOrganizerSettingsForm
  send: Send
  uploadLetter: (file: File) => void
}) {
  const { schema, settings, dirty, saving } = form
  const questionCategoryGroups = groupCustomQuestionCategories(schema.customQuestionCategories)

  return (
    <fieldset className="flex min-w-0 flex-col gap-12 border-0 p-0 pb-20" disabled={saving}>
      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Organizer sections</h3>
          <p className="mt-1 text-base text-gray-700">
            Choose what clients see in their organizer.
          </p>
        </div>
        <EngagementLetterSection
          letter={form.letter}
          saving={saving}
          send={send}
          uploadLetter={uploadLetter}
        />
        <div className="rounded-lg border border-gray-300 p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Questionnaire</h4>
              <p className="mt-1 text-base text-gray-700">
                Show the questionnaire in the client organizer.
              </p>
            </div>
            <ToggleSwitch
              value={settings['hide_questionnaire'] != true}
              aria-label="Show questionnaire"
              onChange={(visible) =>
                send({ type: 'flagChanged', key: 'hide_questionnaire', on: !visible })
              }
            />
          </div>
          {settings['hide_questionnaire'] != true && (
            <div className="mt-5">
              <Accordion>
                <AccordionItem
                  title="Topics & pages"
                  defaultExpanded
                  statusLabel={`${topicCategoryCount(schema)} categories`}
                >
                  <Accordion>
                    {schema.questionnaireTopicSettings.sections.map((section) => (
                      <QuestionnaireTopicsSection
                        key={section.id}
                        section={section}
                        settings={settings}
                        expandedCategoryIds={form.expandedCategoryIds}
                        send={send}
                      />
                    ))}
                  </Accordion>
                </AccordionItem>
                {questionCategoryGroups.custom.length > 0 && (
                  <AccordionItem
                    title="Custom questions"
                    statusLabel={`${questionCategoryGroups.custom.length} groups`}
                  >
                    <p className="mb-3">Add up to 35 custom questions.</p>
                    <Accordion>
                      {questionCategoryGroups.custom.map((category) => (
                        <QuestionsSection
                          key={category.name}
                          category={category}
                          settings={settings}
                          send={send}
                        />
                      ))}
                    </Accordion>
                  </AccordionItem>
                )}
                {questionCategoryGroups.state.length > 0 && (
                  <AccordionItem
                    title="State specific questions"
                    statusLabel={`${questionCategoryGroups.state.length} groups`}
                  >
                    <p className="mb-3">
                      Questions appear only for clients who lived, worked, or earned income in that
                      state during the tax year.
                    </p>
                    <Accordion>
                      {questionCategoryGroups.state.map((category) => (
                        <QuestionsSection
                          key={category.name}
                          category={category}
                          settings={settings}
                          send={send}
                        />
                      ))}
                    </Accordion>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-300 p-6">
          <div>
            <h4 className="text-lg font-semibold text-gray-900">Document uploads</h4>
            <p className="mt-1 text-base text-gray-700">
              Show the document request list and upload section on the organizer dashboard.
            </p>
          </div>
          <ToggleSwitch
            value={settings['hide_document_uploads'] != true}
            aria-label="Show document uploads"
            onChange={(visible) =>
              send({ type: 'flagChanged', key: 'hide_document_uploads', on: !visible })
            }
          />
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Client welcome</h3>
          <p className="mt-1 text-base text-gray-700">
            Personalize the message at the top of the organizer.
          </p>
        </div>
        <div className="rounded-lg border border-gray-300 p-6">
          <TextArea
            aria-label="Client welcome message"
            className="min-h-40"
            value={
              toMaybeStr(settings[CLIENT_ORGANIZER_DASHBOARD_NOTE_KEY]) ??
              DEFAULT_CLIENT_ORGANIZER_DASHBOARD_NOTE
            }
            onChange={(value) =>
              send({ type: 'settingChanged', key: CLIENT_ORGANIZER_DASHBOARD_NOTE_KEY, value })
            }
          />
          <p className="mt-2 text-sm text-gray-700">
            Available variables:{' '}
            <code>$clientname · $year · $orgname · $entityname · $questionnairelink</code>
          </p>
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Identity verification</h3>
          <p className="mt-1 text-base text-gray-700">
            Manage checks for organizer access and tax return signing.
          </p>
        </div>
        <div className="divide-y divide-gray-200 rounded-lg border border-gray-300 px-6">
          <div className="flex items-center justify-between gap-4 py-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Client MFA</h4>
              <p className="mt-1 text-base text-gray-700">
                Require the last 4 digits of the client's SSN after login to access the organizer.
              </p>
            </div>
            <ToggleSwitch
              value={settings[CLIENT_ORGANIZER_MFA_ENABLED_KEY] == true}
              aria-label="Require MFA for clients"
              onChange={(on) =>
                send({ type: 'flagChanged', key: CLIENT_ORGANIZER_MFA_ENABLED_KEY, on })
              }
            />
          </div>
          <div className="flex items-center justify-between gap-4 py-6">
            <div>
              <h4 className="text-lg font-semibold text-gray-900">Tax return signing — KBA</h4>
              <p className="mt-1 text-base text-gray-700">
                Ask each signer knowledge-based identity questions after signing a tax return client
                copy.
              </p>
            </div>
            <ToggleSwitch
              value={settings[TAX_RETURN_DELIVERY_KBA_ENABLED_KEY] == true}
              aria-label="Enable Knowledge Based Authentication"
              onChange={(on) =>
                send({ type: 'flagChanged', key: TAX_RETURN_DELIVERY_KBA_ENABLED_KEY, on })
              }
            />
          </div>
        </div>
      </section>

      <div className="fixed bottom-0 left-0 right-0 z-10">
        <Wizard.BottomNav>
          <div />
          <div className="flex flex-row items-center gap-4">
            {dirty && (
              <div className="flex flex-row items-center gap-2 text-base font-semibold text-red-500">
                <WarningIcon className="mt-0.5 size-4" />
                You have unsaved changes
              </div>
            )}
            <Button
              leadingIcon={saving ? <Spinner className="size-4" /> : undefined}
              variant="primary"
              disabled={!dirty || saving}
              onClick={() => send({ type: 'saveRequested' })}
            >
              Save Settings
            </Button>
          </div>
        </Wizard.BottomNav>
      </div>
    </fieldset>
  )
}

function EngagementLetterSection({
  letter,
  saving,
  send,
  uploadLetter
}: {
  letter: LetterView
  saving: boolean
  send: Send
  uploadLetter: (file: File) => void
}) {
  const upload = (uploading: boolean) => (
    <LetterBody
      action={
        <Button
          type="upload"
          variant="default"
          accept="application/pdf"
          disabled={saving}
          leadingIcon={
            uploading ? <Spinner className="size-4" /> : <CloudUploadIcon className="size-4" />
          }
          onUpload={uploadLetter}
        >
          Upload PDF
        </Button>
      }
    />
  )
  const stored = ({
    letter,
    requireSpouseSignature
  }: Extract<LetterView, { status: 'stored' | 'removing' }>) => (
    <LetterBody
      action={
        <Button
          variant="default"
          leadingIcon={<EyeIcon className="size-4" />}
          onClick={() => send({ type: 'letterPreviewRequested', documentId: letter.documentId })}
        >
          Preview
        </Button>
      }
    >
      <div className="mt-5 flex items-center justify-between gap-4">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">Require spouse signature</h4>
          <p className="mt-1 text-base text-gray-700">
            Both the taxpayer and spouse must sign when filing jointly.
          </p>
        </div>
        <ToggleSwitch
          value={requireSpouseSignature}
          aria-label="Require engagement letter signature from spouse"
          onChange={(value) => send({ type: 'spouseSignatureChanged', value })}
        />
      </div>
    </LetterBody>
  )

  return (
    <div className="rounded-lg border border-gray-300 p-6">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h4 className="text-lg font-semibold text-gray-900">Engagement letter</h4>
          <p className="mt-1 text-base text-gray-700">
            Add your own custom engagement letter for clients to review and sign.
          </p>
        </div>
        <ToggleSwitch
          value={letter.status != 'off'}
          disabled={saving}
          aria-label="Show engagement letter"
          onChange={(on) => {
            if (on) send({ type: 'letterChooserToggled', open: true })
            else if (letter.status == 'stored') send({ type: 'letterRemoveRequested' })
            else send({ type: 'letterChooserToggled', open: false })
          }}
        />
      </div>
      {cases(letter, 'status', {
        off: () => null,
        choosing: () => upload(false),
        uploading: () => upload(true),
        stored,
        removing: stored
      })}
    </div>
  )
}

function LetterBody({ action, children }: { action: ReactNode; children?: ReactNode }) {
  return (
    <div className="mt-5 border-t border-gray-200 pt-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <p className="text-sm leading-relaxed text-gray-700">
          Upload a Letter-size PDF.
          <br />
          Signatures appear at the bottom-right of the last page.
        </p>
        <div className="flex shrink-0 justify-end">{action}</div>
      </div>
      {children}
    </div>
  )
}

function QuestionnaireTopicsSection({
  section,
  settings,
  expandedCategoryIds,
  send
}: {
  section: QuestionnaireTopicSection
  settings: Record<string, JSON>
  expandedCategoryIds: string[]
  send: Send
}) {
  const totalCategoriesCount = sectionCategoryCount(section)

  return (
    <AccordionItem
      title={section.name}
      statusLabel={
        totalCategoriesCount > 1
          ? `${visibleSectionCategoryCount(
              section,
              settings
            )} of ${totalCategoriesCount} categories`
          : undefined
      }
    >
      <div className="flex flex-col gap-5 py-2">
        {section.groups.map((group) => {
          return (
            <div className="flex flex-col gap-4" key={group.id}>
              {group.name && <h5 className="text-sm uppercase text-gray-500">{group.name}</h5>}
              <div className="flex flex-col gap-3">
                {group.categories.map((category) => {
                  const pages = category.pages.filter((page) => page.name)
                  const categoryVisible = isCategoryVisible(category, settings)
                  const pagesExpanded = categoryVisible && expandedCategoryIds.includes(category.id)

                  return (
                    <div className="flex flex-col gap-3 py-2" key={category.id}>
                      <div className="flex flex-row items-center justify-between gap-4">
                        <div className="flex min-w-0 flex-row items-center gap-2 text-base text-gray-900">
                          {pages.length > 0 ? (
                            <button
                              type="button"
                              className="flex items-center gap-x-2 text-left"
                              aria-expanded={pagesExpanded}
                              onClick={() =>
                                send({ type: 'categoryToggled', categoryId: category.id })
                              }
                            >
                              <CaretDownIcon
                                className={`size-4 shrink-0 ${caretClass(
                                  categoryVisible,
                                  pagesExpanded
                                )}`}
                              />
                              <span>{category.name}</span>
                            </button>
                          ) : (
                            <span className="pl-6">{category.name}</span>
                          )}
                        </div>
                        <ToggleSwitch
                          disabled={category.canHide == false}
                          aria-label={`toggle-questionnaire-category-${category.name}-${category.id}`}
                          value={categoryVisible}
                          onChange={(visible) =>
                            send({ type: 'flagChanged', key: category.hideKey!, on: !visible })
                          }
                        />
                      </div>

                      {pages.length > 0 && pagesExpanded ? (
                        <div className="flex flex-col gap-3 pl-6">
                          {pages.map((page) => (
                            <label className="flex flex-row items-center gap-3" key={page.id}>
                              <input
                                type="checkbox"
                                className="size-4 appearance-none rounded border border-stone-300 checked:border-transparent checked:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-50"
                                checked={settings[page.hideKey!] != true}
                                disabled={page.canHide == false}
                                onChange={(e) =>
                                  send({
                                    type: 'flagChanged',
                                    key: page.hideKey!,
                                    on: !e.target.checked
                                  })
                                }
                              />
                              <span className="text-base text-gray-600">{page.name}</span>
                              {page.tip && (
                                <IconTip body={page.tip} icon="info" className="text-gray-400" />
                              )}
                            </label>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </AccordionItem>
  )
}

function QuestionsSection({
  category,
  settings,
  send
}: {
  category: CustomQuestionCategory
  settings: Record<string, JSON>
  send: Send
}) {
  const includedCount = includedQuestionCount(category, settings)

  return (
    <AccordionItem
      title={category.name}
      iconTip={
        category.tip ? <IconTip body={category.tip} icon="info" className="text-gray-400" /> : null
      }
      statusLabel={
        includedCount > 0
          ? `${includedCount} ${inflect(includedCount, 'question')} included`
          : undefined
      }
    >
      <div className="mt-4 flex flex-col gap-4">
        {category.questions.map((question) => {
          return (
            <div className="flex flex-row items-center justify-between gap-2" key={question.key}>
              <div className="text-base text-gray-900">{question.label}</div>
              <ToggleSwitch
                aria-label={`toggle-custom-question-${question.label}-${question.key}`}
                value={settings[question.key] == true}
                onChange={(on) => send({ type: 'flagChanged', key: question.key, on })}
              />
            </div>
          )
        })}
      </div>
    </AccordionItem>
  )
}

function caretClass(visible: boolean, expanded: boolean) {
  if (!visible) return 'invisible'
  return expanded ? 'rotate-0' : '-rotate-90'
}
